import React from 'react';
import {
  Menu,
  MenuItem,
} from '@mui/material';
import Can from '../permissions/Can';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { isLeadDisabledLike } from './utils';
import { shouldShowAssignStatusForManager } from './leadAssignUserHelpers';
import { leadUploadDocumentsPath } from '../../navigations/path';

const LeadActionsMenu = ({
  anchorEl,
  selectedLead,
  onClose,
  onViewLead,
  onEditLead,
  onCreateDba,
  onEditDba,
  onOpenStatusDialog,
  onOpenAssignDialog,
  onOpenShareDialog,
  onOpenAttachmentsDialog,
  onOpenIssueDialog,
  onOpenCreateCustomerDialog,
  onRegenerateCustomerCredentials,
  onOpenAssignedUsersDialog,
  onOpenActivityLogsDialog,
  onDeleteLead,
}) => {
  const navigate = useNavigate();

  const handleView = () => {
    onViewLead(selectedLead.id);
    onClose();
  };

  const handleEdit = () => {
    onEditLead(selectedLead.id);
    onClose();
  };

  const handleCreateDbaClick = () => {
    onCreateDba(selectedLead.id);
    onClose();
  };

  const handleEditDbaClick = () => {
    onEditDba(selectedLead.dba.id);
    onClose();
  };

  const handleDelete = () => {
    onDeleteLead(selectedLead);
    onClose();
  };

  const handleCreateCustomerClick = () => {
    onOpenCreateCustomerDialog(selectedLead);
    onClose();
  };

  const handleRegenerateCredentialsClick = () => {
    onRegenerateCustomerCredentials(selectedLead);
    onClose();
  };

  const handleStatusClick = () => {
    onOpenStatusDialog();
    onClose();
  };

  const handleAssignClick = () => {
    onOpenAssignDialog();
    onClose();
  };

  const handleShareClick = () => {
    onOpenShareDialog();
    onClose();
  };

  const handleAttachmentsClick = () => {
    onOpenAttachmentsDialog();
    onClose();
  };

  const handleUploadDocumentsClick = () => {
    if (!selectedLead?.id) return;
    onClose();
    navigate(leadUploadDocumentsPath(selectedLead.id));
  };

  const handleAssignedUsersClick = () => {
    onOpenAssignedUsersDialog();
    onClose();
  };

  const handleActivityLogsClick = () => {
    onOpenActivityLogsDialog();
    onClose();
  };

  const handleIssueClick = () => {
    onOpenIssueDialog();
    onClose();
  };

  const { user } = useAuth();
  const isLeadLocked = isLeadDisabledLike(selectedLead, user);
  const isManagerAssignedLock = shouldShowAssignStatusForManager(selectedLead, user);

  const userRoleName = (user?.roles?.[0]?.name || user?.role?.name || '').toLowerCase();
  const isLeadGen = userRoleName === 'lead gen';
  const isCloser = userRoleName === 'closer';
  const currentStatusName = (selectedLead?.last_status_log?.status?.name || '').toLowerCase().trim();
  const isEditableStatusForLeadGen =
    currentStatusName === 'follow up (l)' || currentStatusName === 'qc declined';
  const CLOSER_ALLOWED_STATUSES = ['qualified', 'follow up (c)', 'follow c', 'qc approved', 'iuw declined'];
  const isCloserAllowedStatus = CLOSER_ALLOWED_STATUSES.includes(currentStatusName);
  const canShowEdit =
    (!isLeadGen || isEditableStatusForLeadGen) &&
    (!isCloser || isCloserAllowedStatus);

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      <Can permission="lead-view">
        <MenuItem onClick={handleView}>View</MenuItem>
      </Can>
      <Can permission="lead-edit">
        {!isLeadLocked && !isManagerAssignedLock && canShowEdit && (
          <MenuItem onClick={handleEdit}>Edit</MenuItem>
        )}
      </Can>
      {selectedLead?.dba?.id ? (
        <Can permission="dba-edit">
          <MenuItem onClick={handleEditDbaClick}>
            Edit DBA
          </MenuItem>
        </Can>
      ) : (
        <Can permission="dba-create">
          <MenuItem onClick={handleCreateDbaClick}>
            Create DBA
          </MenuItem>
        </Can>
      )}
      <Can permission="lead-status">
        {!isLeadLocked && (
          <MenuItem onClick={handleStatusClick}>Change Status</MenuItem>
        )}
      </Can>
      <Can permission="lead-assign-user">
        {!isLeadLocked && !isManagerAssignedLock && (
          <MenuItem onClick={handleAssignClick}>Assign to User</MenuItem>
        )}
      </Can>
      <Can permission="attachment-upload">
        <MenuItem onClick={handleUploadDocumentsClick}>Upload Document</MenuItem>
      </Can>
      <Can permission="shareable-link-create">
        <MenuItem onClick={handleShareClick}>Create Shareable URL</MenuItem>
      </Can>
      <Can permission="attachment-view">
        <MenuItem onClick={handleAttachmentsClick}>Show All Attachments</MenuItem>
      </Can>
      <Can permission="lead-assigned-users-list">
        <MenuItem onClick={handleAssignedUsersClick}>Assigned Users History</MenuItem>
      </Can>
      <Can permission="lead-log-activity">
        <MenuItem onClick={handleActivityLogsClick}>Lead Activity Logs</MenuItem>
      </Can>
      <Can permission="customer-create">
        <MenuItem onClick={handleCreateCustomerClick}>
          Create Customer
        </MenuItem>
      </Can>
      <Can permission="customer-create">
        <MenuItem onClick={handleRegenerateCredentialsClick}>
          Generate Customer Credential
        </MenuItem>
      </Can>
      <Can permission="issue-create">
        <MenuItem onClick={handleIssueClick}>Create An Issue</MenuItem>
      </Can>
      <Can permission="lead-delete">
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Can>
    </Menu>
  );
};

export default LeadActionsMenu;